import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GUEST_EMAIL_SUFFIX } from '@shared/types';
import { api, type TripSummary } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateRange } from '../lib/format';
import { Landing } from './Landing';

export function HomePage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState<'mine' | 'saved'>('mine');
  const [trips, setTrips] = useState<{ mine: TripSummary[]; saved: TripSummary[] } | null>(null);

  useEffect(() => {
    if (!user) return;
    void api.listTrips().then(setTrips);
  }, [user]);

  if (loading) return <div className="mx-auto max-w-2xl px-5 py-16" />;
  if (!user) return <Landing />;
  if (!trips) return <div className="mx-auto max-w-2xl px-5 py-16" />;

  // Under open access every visitor is silently a guest, so "logged out" never
  // happens — the sales page shows for guests who have nothing yet instead.
  const isGuest = user.email.endsWith(GUEST_EMAIL_SUFFIX);
  if (isGuest && trips.mine.length === 0 && trips.saved.length === 0) {
    return <Landing />;
  }

  const list = tab === 'mine' ? trips.mine : trips.saved;

  return (
    <main className="mx-auto max-w-2xl px-5 py-10">
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex gap-4">
          {(['mine', 'saved'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`font-util text-[1.0625rem] transition-colors ${
                tab === t ? 'font-semibold text-ink' : 'text-muted hover:text-ink'
              }`}
            >
              {t === 'mine' ? 'My trips' : 'Wishlist'}
              {t === 'saved' && trips.saved.length > 0 && (
                <span className="ml-1.5 font-normal text-muted">{trips.saved.length}</span>
              )}
            </button>
          ))}
        </div>
        <Link to="/trips/new" className="btn-primary shrink-0">
          New trip
        </Link>
      </div>

      {trips.mine.length === 0 && trips.saved.length > 0 && (
        <p className="mt-6 rounded-note bg-raised px-4 py-3 font-body text-[0.9375rem] text-ink/85">
          Been somewhere people ask about?{' '}
          <Link to="/trips/new" className="text-accent underline">
            Start your page
          </Link>
          . It takes about three minutes of talking.
        </p>
      )}

      {list.length === 0 ? (
        <p className="mt-12 font-body text-muted">
          {tab === 'mine'
            ? 'Start with a trip you have already taken. The ones people ask you about.'
            : 'Trips you save from friends land here.'}
        </p>
      ) : (
        <div className="mt-6 divide-y divide-ink/8">
          {list.map((trip) => (
            <Link key={trip.id} to={`/t/${trip.slug}`} className="flex gap-4 py-4 group">
              {trip.coverImage ? (
                <img
                  src={trip.coverImage}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-img object-cover bg-raised"
                />
              ) : (
                <div className="h-16 w-16 shrink-0 rounded-img bg-raised" aria-hidden="true" />
              )}
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-[1.25rem] text-ink group-hover:text-accent transition-colors">
                  {trip.title}
                </h2>
                <p className="mt-0.5 font-util text-meta text-muted">
                  {[
                    trip.destination,
                    formatDateRange(trip.startDate, trip.endDate),
                    `${trip.itemCount} ${trip.itemCount === 1 ? 'place' : 'places'}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {trip.owner && (
                  <p className="mt-1 font-util text-meta text-muted">
                    from {trip.owner.name ?? 'a friend'}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
